/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';

import { withNotification } from 'components/Notification';
import { Toggle } from 'components/Toggle';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import { apiErrorHandler, NotificationTypes } from 'utils';
import {
  SAVE_RETURN_NOTIFICATIONS_SETTINGS,
  SET_RETURN_KLAVIYO_FLOW_STATUS,
} from 'gql/mutations';
import {
  GET_KLAVIYO_CONNECTION,
  GET_RETURN_KLAVIYO_FLOWS_STATUS,
  GET_RETURN_KLAVIYO_FLOW_SEND_COUNTS,
} from 'gql/queries';
import {
  ReturnCustomerLifecycleNotificationEvent,
  ReturnNotificationsSettingsV2,
} from 'types/return';
import { returnEmailTemplateTypes } from './data/templates-data';
import {
  EmailNotificationTemplates,
  KlaviyoFlowForEventType,
} from './EmailNotificationTemplates';
import { ReturnEmailTemplateSection } from './ReturnEmailTemplateSection';

type ReturnNotificationTemplatesProps = {
  embedded?: boolean;
};

const CUSTOMER_LIFECYCLE_EVENTS: ReturnCustomerLifecycleNotificationEvent[] = [
  'RETURN_CREATED',
  'RETURN_APPROVED',
  'RETURN_DENIED',
  'RETURN_UPDATED',
  'LABEL_CREATED',
  'REMINDER_SENT',
  'RETURN_EXPIRED',
  'REFUND_PROCESSED',
  'STORE_CREDIT_ISSUED',
];
const LEGACY_CUSTOMER_LIFECYCLE_EVENTS = CUSTOMER_LIFECYCLE_EVENTS.filter(
  (eventType) => eventType !== 'RETURN_UPDATED',
);
type LifecyclePreferences = ReturnNotificationsSettingsV2['customerLifecycle'];
const canonicalLifecycle = (value: any): LifecyclePreferences | null => {
  const lifecycle = value?.customerLifecycle;
  return value?.version === 2 && typeof lifecycle?.enabled === 'boolean' &&
    Array.isArray(lifecycle.events) && lifecycle.events.every((event: unknown) =>
      CUSTOMER_LIFECYCLE_EVENTS.some((allowed) => allowed === event))
    ? { enabled: lifecycle.enabled, events: [...lifecycle.events] } : null;
};
const sameLifecycle = (left: LifecyclePreferences, right: LifecyclePreferences) =>
  left.enabled === right.enabled &&
  new Set(left.events).size === new Set(right.events).size &&
  left.events.every((event) => right.events.includes(event));

export const ReturnNotificationTemplates = withNotification(
  ({
    showNotification,
    embedded = false,
  }: ReturnNotificationTemplatesProps & any) => {
    const { storeId, storeProperties, refetchStoreProperties } = useStore();
    const settings: ReturnNotificationsSettingsV2 | null =
      storeProperties?.returnNotificationsSettings ?? null;
    const sourceSettings = useRef(settings);
    sourceSettings.current = settings;
    const [confirmedLifecycle, setConfirmedLifecycle] = useState<{
      storeId: string; value: LifecyclePreferences; source: ReturnNotificationsSettingsV2 | null;
    } | null>(null);
    const [reloadStoreId, setReloadStoreId] = useState<string | null>(null);
    const reloadRequired = Boolean(storeId && reloadStoreId === storeId);
    // Retain the acknowledged value until the store context reflects the verified read.
    const confirmed = confirmedLifecycle && confirmedLifecycle.storeId === storeId &&
      (reloadRequired || confirmedLifecycle.source === settings) ? confirmedLifecycle.value : null;
    const customerLifecycleEnabled =
      confirmed ? confirmed.enabled : settings?.customerLifecycle?.enabled !== false;
    const savedEvents = settings?.customerLifecycle?.events;
    const enabledEvents = confirmed ? confirmed.events : Array.isArray(savedEvents)
      ? savedEvents
      : settings === null
        ? CUSTOMER_LIFECYCLE_EVENTS
        : LEGACY_CUSTOMER_LIFECYCLE_EVENTS;
    const [savingEvent, setSavingEvent] = useState<string | null>(null);
    const [saveReturnNotificationsSettings] = useMutation(
      SAVE_RETURN_NOTIFICATIONS_SETTINGS,
    );

    const { data: klaviyoConnectionData } = useQuery(GET_KLAVIYO_CONNECTION, {
      variables: { storeId },
      skip: !storeId,
      fetchPolicy: 'network-only',
    });
    const isKlaviyoConnected = Boolean(
      klaviyoConnectionData?.klaviyoConnection?.connected,
    );

    const { data: returnKlaviyoFlowsStatusData } = useQuery(
      GET_RETURN_KLAVIYO_FLOWS_STATUS,
      {
        variables: { storeId },
        skip: !storeId || !isKlaviyoConnected,
        fetchPolicy: 'network-only',
      },
    );
    const returnKlaviyoFlowStatuses: {
      flowId: string;
      name: string;
      status: string;
      eventType: string;
    }[] = returnKlaviyoFlowsStatusData?.returnKlaviyoFlowsStatus || [];

    const [sendCountWindow, setSendCountWindow] = useState<number>(30);
    const sendCountRange = useMemo(
      () => ({
        since: moment()
          .subtract(sendCountWindow, 'days')
          .format('YYYY-MM-DDTHH:mm:ss'),
        until: moment().format('YYYY-MM-DDTHH:mm:ss'),
      }),
      [sendCountWindow],
    );

    const { data: returnKlaviyoSendCountsData } = useQuery(
      GET_RETURN_KLAVIYO_FLOW_SEND_COUNTS,
      {
        variables: { storeId, ...sendCountRange },
        skip:
          !storeId || !isKlaviyoConnected || !returnKlaviyoFlowStatuses.length,
        fetchPolicy: 'network-only',
      },
    );
    const returnKlaviyoSendCounts: { flowId: string; sendCount: number }[] =
      returnKlaviyoSendCountsData?.returnKlaviyoFlowSendCounts || [];

    const klaviyoFlowsByEventType = useMemo(() => {
      const map: Record<string, KlaviyoFlowForEventType> = {};
      if (!isKlaviyoConnected) return map;

      // Klaviyo-only merged pseudo types — the settings table still has separate rows per real
      // event type below (merchants mute REFUND_PROCESSED/STORE_CREDIT_ISSUED and
      // RETURN_APPROVED/RETURN_DENIED independently), so each pseudo type's one shared flow needs
      // to resolve under both real event types it stands in for.
      const PSEUDO_EVENT_TYPE_ALIASES: Record<string, string[]> = {
        RETURN_PROCESSED: ['REFUND_PROCESSED', 'STORE_CREDIT_ISSUED'],
        RETURN_REVIEWED: ['RETURN_APPROVED', 'RETURN_DENIED'],
      };

      returnKlaviyoFlowStatuses.forEach((flow) => {
        // Resolution delivery requires saved email consent independently of flow status.
        if (flow.eventType === 'RETURN_UPDATED') return;
        const entry = {
          flowId: flow.flowId,
          status: flow.status,
          sendCount: returnKlaviyoSendCounts.find(
            (count) => count.flowId === flow.flowId,
          )?.sendCount,
        };
        const aliases = PSEUDO_EVENT_TYPE_ALIASES[flow.eventType];
        if (aliases) {
          aliases.forEach((eventType) => {
            map[eventType] = entry;
          });
        } else {
          map[flow.eventType] = entry;
        }
      });
      return map;
    }, [
      isKlaviyoConnected,
      returnKlaviyoFlowStatuses,
      returnKlaviyoSendCounts,
    ]);

    const [togglingKlaviyoFlowId, setTogglingKlaviyoFlowId] = useState<
      string | null
    >(null);
    const [setReturnKlaviyoFlowStatus] = useMutation(
      SET_RETURN_KLAVIYO_FLOW_STATUS,
      { refetchQueries: ['returnKlaviyoFlowsStatus'] },
    );

    const handleToggleKlaviyoFlow = async (
      flow: KlaviyoFlowForEventType,
      turnOn: boolean,
    ) => {
      if (
        !storeId ||
        !customerLifecycleEnabled ||
        reloadRequired ||
        savingEvent ||
        togglingKlaviyoFlowId
      ) return;
      setTogglingKlaviyoFlowId(flow.flowId);
      try {
        const { data } = await setReturnKlaviyoFlowStatus({
          variables: {
            storeId,
            flowId: flow.flowId,
            status: turnOn ? 'live' : 'draft',
          },
        });
        if (!data?.setReturnKlaviyoFlowStatus) {
          showNotification(
            NotificationTypes.error,
            "Klaviyo couldn't update this flow. Check its email content in Klaviyo and try again.",
          );
        }
      } catch (error: any) {
        const newError = apiErrorHandler(error);
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            "Klaviyo couldn't update this flow. Check its email content in Klaviyo and try again.",
        );
      } finally {
        setTogglingKlaviyoFlowId(null);
      }
    };

    const userTemplates = CUSTOMER_LIFECYCLE_EVENTS.map((eventType) => ({
      id: eventType,
      eventType,
      active: customerLifecycleEnabled && enabledEvents.includes(eventType),
      sentCount: 0,
    }));

    const refreshPreferences = async (expected: LifecyclePreferences, targetStoreId: string) => {
      const result = await refetchStoreProperties?.();
      const refreshedStore = result?.data?.store;
      const refreshed = canonicalLifecycle(refreshedStore?.returnNotificationsSettings);
      if (result?.error || result?.errors?.length || refreshedStore?.id !== targetStoreId ||
        !refreshed || !sameLifecycle(expected, refreshed)) {
        throw new Error('Saved email preferences could not be verified. Reload before changing them.');
      }
      setConfirmedLifecycle({ storeId: targetStoreId, value: refreshed, source: sourceSettings.current });
      setReloadStoreId((current) => current === targetStoreId ? null : current);
    };

    const reloadPreferences = async () => {
      if (!storeId || savingEvent || togglingKlaviyoFlowId || confirmedLifecycle?.storeId !== storeId) return;
      setSavingEvent('reload');
      try { await refreshPreferences(confirmedLifecycle.value, storeId); }
      catch (error: any) { showNotification(NotificationTypes.error, error.message); }
      finally { setSavingEvent(null); }
    };

    const handleToggleEvent = async (template: { eventType: string }) => {
      if (!customerLifecycleEnabled || reloadRequired) return;
      const eventType =
        template.eventType as ReturnCustomerLifecycleNotificationEvent;
      const nextEvents = enabledEvents.includes(eventType)
        ? enabledEvents.filter((event) => event !== eventType)
        : [...enabledEvents, eventType];

      await saveLifecyclePreferences(
        customerLifecycleEnabled,
        nextEvents,
        eventType,
      );
    };

    const saveLifecyclePreferences = async (
      enabled: boolean,
      events: ReturnCustomerLifecycleNotificationEvent[],
      savingKey: string,
    ) => {
      if (!storeId || savingEvent || togglingKlaviyoFlowId || reloadRequired) return;
      setSavingEvent(savingKey);
      let acknowledged: LifecyclePreferences | null = null;
      try {
        const response = await saveReturnNotificationsSettings({
          variables: {
            input: {
              storeId,
              customerLifecycle: {
                enabled,
                events,
              },
            },
          },
        });
        if (response.errors?.length) throw new Error('The email preference update was not confirmed. Please try again.');
        acknowledged = canonicalLifecycle(response.data?.saveReturnNotificationsSettings?.returnNotificationsSettings);
        if (!acknowledged) throw new Error('The email preference update was not confirmed. Please try again.');
        setConfirmedLifecycle({ storeId, value: acknowledged, source: sourceSettings.current });
        await refreshPreferences(acknowledged, storeId);
      } catch (error: any) {
        if (acknowledged) setReloadStoreId(storeId);
        const newError = apiErrorHandler(error);
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            'Something went wrong updating this notification, please try again.',
        );
      } finally {
        setSavingEvent(null);
      }
    };

    return (
      <div className={classNames(embedded && 'space-y-6')}>
        {isKlaviyoConnected ? (
          <ReturnEmailTemplateSection
            storeId={storeId}
            connectionTemplateStyle={
              klaviyoConnectionData?.klaviyoConnection?.returnTemplateStyle
            }
            showNotification={showNotification}
            sendCountWindow={sendCountWindow}
            onChangeSendCountWindow={setSendCountWindow}
          />
        ) : null}
        <div className="space-y-2">
          <Toggle
            ariaLabel="Enable return and exchange emails"
            leftLabel="Enable return and exchange emails"
            isEnabled={customerLifecycleEnabled}
            disabled={!storeId || reloadRequired || Boolean(savingEvent || togglingKlaviyoFlowId)}
            onChange={(enabled) =>
              void saveLifecyclePreferences(
                enabled,
                [...enabledEvents],
                'customerLifecycle',
              )
            }
            isSmall
          />
          {!customerLifecycleEnabled && (
            <p role="status" className="text-sm text-gray-600">
              Return and exchange emails are paused. Saved event preferences are unchanged.
            </p>
          )}
          {savingEvent && (
            <p role="status" className="text-sm text-gray-600">
              Saving return and exchange email preferences…
            </p>
          )}
        </div>
        {reloadRequired && <div role="alert" className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p>Your change was saved, but email preferences could not be refreshed. Verify the saved preferences before changing them.</p>
          <div className="flex gap-3">
            <Button variant="outlined" disabled={Boolean(savingEvent)} onClick={reloadPreferences}>Reload email preferences</Button>
            <Button variant="outlined" disabled={Boolean(savingEvent)} onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>}
        <fieldset
          disabled={!storeId || !customerLifecycleEnabled || reloadRequired || Boolean(savingEvent || togglingKlaviyoFlowId)}
          className="min-w-0"
        >
          <legend className="sr-only">Customer return and exchange email events</legend>
          <EmailNotificationTemplates
            userTemplates={userTemplates}
            isLoading={false}
            onChangeActiveTemplate={handleToggleEvent}
            embedded={embedded}
            typeMap={returnEmailTemplateTypes}
            klaviyoFlowsByEventType={customerLifecycleEnabled ? klaviyoFlowsByEventType : {}}
            onToggleKlaviyoFlow={handleToggleKlaviyoFlow}
            togglingKlaviyoFlowId={togglingKlaviyoFlowId}
          />
        </fieldset>
      </div>
    );
  },
);
